import {
    Flex,
    IconButton,
    Image,
    Popover,
    PopoverBody,
    PopoverContent,
    PopoverHeader,
    PopoverTrigger,
    Text,
} from '@chakra-ui/react';
import { useContext, useEffect } from 'react';
import StreamGoldIcon from '../../assets/store/StreamGold.png';
import { AuthenticatedComponent } from '../../auth/keycloak';
import PageDataContext from '../../context/PageDataContext';
import { useSessionStorage } from '../../hooks/useSessionStorage';
import { getUserInventory, InventoryDef } from '../../service/inventory';
import { InformationIcon } from '../iconComponents/InformationIcon';

interface CurrencyForPayoutProps {
    refreshFlag: boolean | undefined;
    quantityCallback: (available: number, pending: number) => void;
}

export const CurrencyForPayout: AuthenticatedComponent<CurrencyForPayoutProps> = ({
    keycloak,
    refreshFlag,
    quantityCallback,
}) => {
    const { isLoadFromCache } = useContext(PageDataContext);

    const [totalGold, setTotalGold] = useSessionStorage<number>('streamgold_payout_total', 0);

    const [availableTotal, setAvailableTotal] = useSessionStorage<number>(
        'streamgold_payout_available_total',
        0,
    );
    const [pendingFromDonation, setPendingFromDonation] = useSessionStorage<number>(
        'streamgold_payout_pending_donation',
        0,
    );
    const [pendingFromPurchase, setPendingFromPurchase] = useSessionStorage<number>(
        'streamgold_payout_pending_purchase',
        0,
    );

    const [refItem, setReferenceItem] = useSessionStorage<InventoryDef | undefined>(
        'streamgold_ref_item',
        undefined,
    );

    const getInventory = async () => {
        try {
            const invs = await getUserInventory(
                keycloak.subject,
                'streamsix_currency',
                '',
                keycloak.token,
            );

            let total = 0;
            let pendingPurchase = 0;
            let pendingDonation = 0;
            let available = 0;

            const currentDate = new Date();

            invs.forEach((inv) => {
                setReferenceItem(inv);
                total += inv.quantity;
                if (!inv.inventory_key.pending) {
                    available += inv.quantity;
                } else {
                    const releaseDate = new Date(inv.inventory_key.pending.release_date);
                    if (currentDate > releaseDate) {
                        available += inv.quantity;
                    } else {
                        inv.inventory_key.pending.source === 'donation'
                            ? (pendingDonation += inv.quantity)
                            : (pendingPurchase += inv.quantity);
                    }
                }
            });
            setTotalGold(total);
            setPendingFromDonation(pendingDonation);
            setPendingFromPurchase(pendingFromPurchase);
            setAvailableTotal(available);

            quantityCallback(available, pendingDonation + pendingFromPurchase);
        } catch (err: any) {
            console.error(err);
        }
    };

    useEffect(() => {
        if (!isLoadFromCache) {
            getInventory();
        }
    }, []);

    useEffect(() => {
        refreshFlag = false;
        getInventory();
    }, [refreshFlag]);

    return (
        <Flex className={'payout-currency'}>
            <Flex
                className={'currency-balance'}
                key={`user_inventory_${0}`}
                data-cy={'user_inventory'}
            >
                <Text className={'balance-display'}>Total Balance:</Text>
                <Flex className={'balance-flex'}></Flex>
                <Image src={StreamGoldIcon} />
                <Text className={'balance-streamgold'}>{`${availableTotal + totalGold} ${
                    refItem?.inventory_item.item_name || 'STREAMGOLD'
                }`}</Text>
            </Flex>
            <Flex className={'currency-balance'}>
                <Text className={'balance-display'}>Pending from Donation:</Text>
                <Flex className={'balance-flex'}></Flex>
                <Image src={StreamGoldIcon} />
                <Text className={'balance-streamgold'}>{`${availableTotal + pendingFromDonation} ${
                    refItem?.inventory_item.item_name || 'STREAMGOLD'
                }`}</Text>
            </Flex>
            <Flex className={'currency-balance'}>
                <Text className={'balance-display'}>Pending from Purchase:</Text>
                <Flex className={'balance-flex'}></Flex>
                <Image src={StreamGoldIcon} />
                <Text className={'balance-streamgold'}>{`${availableTotal + pendingFromPurchase} ${
                    refItem?.inventory_item.item_name || 'STREAMGOLD'
                }`}</Text>
            </Flex>
            <Flex
                className={'currency-balance'}
                key={`user_inventory_${1}`}
                data-cy={'user_inventory'}
            >
                <Text className={'balance-display'}>Available for Withdrawal:</Text>
                <Popover>
                    <PopoverTrigger>
                        <IconButton
                            variant={'unstyled'}
                            icon={<InformationIcon />}
                            aria-label={'available-balance'}
                        />
                    </PopoverTrigger>
                    <PopoverContent>
                        <PopoverHeader>Available balance:</PopoverHeader>
                        <PopoverBody>
                            STREAMGOLD received will be in a pending state for 30 days, once cleared
                            it will become available.
                        </PopoverBody>
                    </PopoverContent>
                </Popover>
                <Flex className={'balance-flex'}></Flex>
                <Image src={StreamGoldIcon} />
                <Text className={'balance-streamgold'}>{`${availableTotal} ${
                    refItem?.inventory_item.item_name || 'STREAMGOLD'
                }`}</Text>
            </Flex>
        </Flex>
    );
};
